import _getIterator from 'babel-runtime/core-js/get-iterator';
import get from 'lodash/get';
import moment from 'moment';
import { isAllDayEvent } from './event';
import translations from '../translations';
import constants from '../constants';

export var estimatedTimeUnitsBySecond = 1000000000;

export var defaultStartTime = { hour: 9, minute: 0

  // dateStr must be a string representing a date in the format
  // YYYY-MM-DD
};export var toDate = function toDate(dateStr) {
  return moment(dateStr, 'YYYY-MM-DD').toDate();
};

// dateStr must be a string representing a date in the format
// YYYY-MM-DD
export var addToDate = function addToDate(dateStr, numDays) {
  return moment(dateStr, 'YYYY-MM-DD').add(numDays, 'day').format('YYYY-MM-DD');
};

export var format = function format(dateStr) {
  return moment(dateStr, 'YYYY-MM-DD').format('YYYY-MM-DD');
};

/* will return (n) if (n) is not a number */
export var nbToStr2digit = function nbToStr2digit(n) {
  if (typeof n !== 'number') {
    return n;
  }
  return n > 9 ? '' + n : '0' + n;
};

/* hour could be a string or number */
export var convertHourTo24hFormat = function convertHourTo24hFormat(hour, period) {
  var maxHour = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 12;

  hour = parseInt(hour);
  if (hour === maxHour && period === 'am') {
    return 0;
  } else if (hour < maxHour && period === 'pm') {
    return hour + 12;
  }
  return hour;
};

/* hour could be a string or number */
export var convertHourTo12hFormat = function convertHourTo12hFormat(hour) {
  var invalid = { hour: null, period: null };
  var period = 'am';
  hour = parseInt(hour);
  if (isNaN(hour)) {
    return invalid;
  }

  if (!(hour >= 0 && hour <= 23)) {
    return invalid;
  }

  if (hour >= 12) {
    period = 'pm';
  }
  if (hour > 12) {
    hour -= 12;
  }

  if (hour === 0) {
    hour = 12;
  }

  return { hour: hour, period: period };
};

export var isValid = function isValid(dateStr) {
  return moment(dateStr, 'YYYY-MM-DD').isValid();
};

export var getNanosecondsFromHourAndMinute = function getNanosecondsFromHourAndMinute(_ref) {
  var hour = _ref.hour,
      minute = _ref.minute;

  hour = parseInt(hour);
  minute = parseInt(minute);
  if (isNaN(hour) || isNaN(minute)) {
    return 0;
  }
  var seconds = hour * 60 * 60;
  seconds += minute * 60;

  return estimatedTimeUnitsBySecond * seconds;
};

export var getHourAndMinuteFromNanoseconds = function getHourAndMinuteFromNanoseconds(nanoseconds) {
  nanoseconds = parseInt(nanoseconds);
  var seconds = nanoseconds / estimatedTimeUnitsBySecond;
  if (isNaN(seconds)) {
    return { hour: null, minute: null };
  }
  var hour = Math.floor(seconds / 3600);
  var minute = Math.floor(seconds % 3600 / 60);

  return { hour: hour, minute: minute };
};

export var convertNanosecondsToMinute = function convertNanosecondsToMinute(nanoseconds) {
  return nanoseconds / 60000000000;
};
export var convertMinuteToNanoseconds = function convertMinuteToNanoseconds(nanoseconds) {
  return nanoseconds * 60000000000;
};

/* value, min, max can be a number or string */
export var isHourOrMinuteBetweenLimits = function isHourOrMinuteBetweenLimits(value, min, max) {
  var intValue = parseInt(value);
  min = parseInt(min);
  max = parseInt(max);

  /* invalid if the intValue is not number */
  if (isNaN(intValue) || isNaN(min) || isNaN(max)) {
    return false;
  }

  /*
   ** if value is a string '0', we consider it valid because the user could write a second digit between min & max
   */
  if (value === '0') {
    return true;
  }

  if (intValue < min || intValue > max) {
    return false;
  }

  return true;
};

/* expect params to be a formatted date string YYYY-MM-DD, but also accept random string (will be last) */
export var compareDates = function compareDates(dateAStr, dateBStr) {
  var dateA = moment(dateAStr, 'YYYY-MM-DD', true);
  var dateB = moment(dateBStr, 'YYYY-MM-DD', true);
  if (!dateA.isValid()) {
    return 1;
  } else if (!dateB.isValid()) {
    return -1;
  }
  return dateB.diff(dateA, 'days');
};

/* nanoduration should be in nanoseconds */
export var beautifyDuration = function beautifyDuration(nanoduration) {
  var showZero = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  var _getHourAndMinuteFrom = getHourAndMinuteFromNanoseconds(nanoduration),
      hour = _getHourAndMinuteFrom.hour,
      minute = _getHourAndMinuteFrom.minute;

  var result = '';
  /* if duration is 0mins, return an empty string */
  if (!hour && !minute && !showZero) {
    return result;
  }

  if (hour > 0) {
    result = nbToStr2digit(hour) + ':' + nbToStr2digit(minute) + 'h';
  } else {
    result = minute + 'min';
  }
  return result;
};

/* expect a date string */
export var beautifyTime = function beautifyTime(dateStr) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '12h';

  if (!dateStr) {
    return '';
  }
  var momentFormat = 'hh:mm A';
  if (format === '24h') {
    momentFormat = 'HH:mm';
  }
  return moment(dateStr).format(momentFormat);
};

export var timeAgo = function timeAgo(dateStr) {
  var maxDiff = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 7;

  if (!dateStr) {
    return '';
  }
  var today = moment();
  var diff = today.diff(dateStr, 'days');

  if (diff <= maxDiff) {
    return moment(dateStr).fromNow();
  }
  return humanizeDate(dateStr);
};

/* nanoduration should be in nanoseconds */
export var beautifyStartTime = function beautifyStartTime(dateStr) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '12h';

  var momentFormat = 'hh:mm A';
  if (format === '24h') {
    momentFormat = 'HH:mm';
  }
  var str = humanizeDate(dateStr);
  str += ' - ' + moment(dateStr).format(momentFormat);
  return str;
};

/* nanoduration should be in nanoseconds */
export var beautifyPinTimeMobile = function beautifyPinTimeMobile(dateStr, nanoduration) {
  var format = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '12h';

  var momentFormat = 'hh:mm A';
  if (format === '24h') {
    momentFormat = 'HH:mm';
  }
  var str = moment(dateStr).format(momentFormat);

  return str;
};

/* if period is null, we expect hour to be a 0-24h number */
export var createDateTimeStrFromDigitsAndPeriod = function createDateTimeStrFromDigitsAndPeriod(dateStr, hourValue, minuteValue) {
  var period = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';

  var date = moment(dateStr);
  period = period.toLowerCase();

  var hours = !isNaN(parseInt(hourValue, 10)) ? parseInt(hourValue, 10) : 0;
  var minutes = !isNaN(parseInt(minuteValue, 10)) ? parseInt(minuteValue, 10) : 0;
  if (period === 'pm' && hours < 12) {
    hours += 12;
  } else if (period === 'am' && hours === 12) {
    hours = 0;
  }
  date.hours(hours);
  date.minutes(minutes);

  return date.format();
};

export var isFullDateTimeStr = function isFullDateTimeStr(dateStr) {
  if (!dateStr || !isValid(dateStr)) {
    return false;
  }
  return moment(dateStr).format() === dateStr;
};

export var isToday = function isToday(dateStr) {
  if (!dateStr || !isValid(dateStr)) {
    return false;
  }
  var date = moment(dateStr);

  return isSameDate(date, moment());
};

export var isTomorrow = function isTomorrow(dateStr) {
  if (!dateStr || !isValid(dateStr)) {
    return false;
  }
  var date = moment(dateStr);
  var tomorrow = moment().add(1, 'days').startOf('day');

  return isSameDate(date, tomorrow);
};

export var isYesterday = function isYesterday(dateStr) {
  if (!dateStr || !isValid(dateStr)) {
    return false;
  }
  var date = moment(dateStr);
  var yesterday = moment().subtract(1, 'days').startOf('day');

  return isSameDate(date, yesterday);
};

export var isOnboarding = function isOnboarding(dateStr) {
  return dateStr === 'Onboarding';
};

export var isSameDate = function isSameDate(date1, date2) {
  date1 = moment(date1);
  date2 = moment(date2);
  return date1.isSame(date2, 'day') && date1.isSame(date2, 'month') && date1.isSame(date2, 'year');
};

export var isSameWeek = function isSameWeek(date1, date2) {
  date1 = moment(date1);
  date2 = moment(date2);
  return date1.isSame(date2, 'week') && date1.isSame(date2, 'month') && date1.isSame(date2, 'year');
};
export var isBefore = function isBefore(date1, date2) {
  date1 = moment(date1);
  date2 = moment(date2);
  return date1.isBefore(date2, 'day');
};

export var isAfter = function isAfter(date1, date2) {
  date1 = moment(date1);
  date2 = moment(date2);
  return date1.isAfter(date2, 'day');
};

export var getShortWeekDay = function getShortWeekDay(dateStr) {
  var date = moment(dateStr, 'YYYY-MM-DD');
  if (!date.isValid()) {
    return null;
  }

  return date.format('ddd');
};

export var humanizeDate = function humanizeDate(dateStr) {
  if (!dateStr) {
    return null;
  }
  var date = moment(dateStr, 'YYYY-MM-DD');
  if (!date.isValid()) {
    return null;
  }

  var today = moment();
  return humanizeDateFromToday(date, today);
};

export var humanizeDateFromToday = function humanizeDateFromToday(date, today) {
  var startOfToday = today.clone().startOf('day');
  var startOfYesterday = startOfToday.clone().subtract(1, 'days');
  var startOfTomorrow = startOfToday.clone().add(1, 'days');

  if (isSameDate(startOfToday, date)) {
    return translations.general.today;
  } else if (isSameDate(startOfTomorrow, date)) {
    return translations.general.tomorrow;
  } else if (isSameDate(startOfYesterday, date)) {
    return translations.general.yesterday;
  } else if (isSameWeek(startOfToday, date)) {
    /* we need to compare the same moment of the day */
    return date.clone().startOf('day').from(startOfToday);
  } else if (isBefore(date, startOfToday)) {
    return date.format('ddd, MMM DD');
  }
  return date.format('MMM DD');
};

export var beautifyDate = function beautifyDate(dateStr) {
  return moment(dateStr).format('MMM DD');
};

export var humanizeDateTime = function humanizeDateTime(dateStr, dateTimeStr) {
  var str = humanizeDate(dateStr);
  if (dateTimeStr) {
    str = humanizeDate(dateTimeStr) + ' at ' + beautifyTime(dateTimeStr);
  }
  return str;
};

export var getGroupForItem = function getGroupForItem(item, groupBy) {
  if (!item || !groupBy) {
    return null;
  }
  var today = moment();
  var tomorrow = moment().add(1, 'days').startOf('day');

  var dateStr = get(item, groupBy);
  if (!dateStr) {
    return 'backlog';
  }
  if (dateStr === 'Onboarding') {
    return 'onboarding';
  }
  var date = moment(dateStr);

  if (today.isAfter(date) || today.isSame(date, 'today')) {
    return 'today';
  } else if (tomorrow.isSame(date, 'day')) {
    return 'tomorrow';
  }
  return 'upcoming';
};

export var getGroupForDate = function getGroupForDate(dateStr) {
  if (!dateStr || !dateStr.length || dateStr === 'backlog') {
    return 'backlog';
  }
  if (dateStr === 'Onboarding') {
    return 'onboarding';
  }
  var date = moment(dateStr);
  var today = moment();
  var tomorrow = moment().add(1, 'days').startOf('day');

  if (moment().isAfter(date) || today.isSame(date, 'day')) {
    return 'today';
  } else if (tomorrow.isSame(date, 'day')) {
    return 'tomorrow';
  } else {
    return 'upcoming';
  }
};

export var isGroupToday = function isGroupToday(group) {
  return group === 'today';
};

export var isGroupTomorrow = function isGroupTomorrow(group) {
  return group === 'tomorrow';
};

export var isGroupUpcoming = function isGroupUpcoming(group) {
  return group === 'upcoming';
};

export var isGroupOnboarding = function isGroupOnboarding(group) {
  return group === 'onboarding';
};

export var isGroupUpdates = function isGroupUpdates(group) {
  return group === 'updates';
};

/*
 * groupBy can be a string (item param)
 * or an array of item params : this first found value will be taken
 * exemple : [ETA.date, ETA.time]
 */
export var groupItemsByDate = function groupItemsByDate(items, groupBy) {
  var result = {
    onboarding: {
      list: []
    },
    today: {
      allDayEvents: [],
      list: []
    },
    tomorrow: {
      allDayEvents: [],
      list: []
    },
    upcoming: {
      list: []
    },
    backlog: {
      list: []
    }
  };

  if (!Array.isArray(groupBy)) {
    groupBy = [groupBy];
  }
  for (var i = 0, l = items.length; i < l; ++i) {
    var item = items[i];
    var date = void 0;
    for (var j = 0, k = groupBy.length; j < k; ++j) {
      date = get(item, groupBy[j]);
      if (date) {
        break;
      }
    }
    var group = getGroupForDate(date);
    if (isAllDayEvent(item)) {
      result[group].allDayEvents.push(item);
    } else {
      result[group].list.push(item);
    }
  }
  return result;
};

export var insertAtLastIndexOfDate = function insertAtLastIndexOfDate(itemToInsert, items) {
  if (!itemToInsert.when || !itemToInsert.when.date) {
    items.push(itemToInsert);
    return items;
  }
  var date = itemToInsert.when.date;
  var index = 0;
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = _getIterator(items), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var item = _step.value;

      if (!item.when || !item.when.date) {
        items.splice(index, 0, itemToInsert);
        return items;
      }
      var comp = compareDates(date, item.when.date);
      if (comp <= 0) {
        index++;
        continue;
      } else {
        items.splice(index, 0, itemToInsert);
        return items;
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  items.push(itemToInsert);
  return items;
};

export var getDateForGroup = function getDateForGroup(group) {
  switch (group) {
    case 'today':
      var today = moment();
      return today.format('YYYY-MM-DD');
    case 'tomorrow':
      var tomorrow = moment().add(1, 'days').startOf('day');
      return tomorrow.format('YYYY-MM-DD');
    case 'upcoming':
      var firstUpcomingDay = moment().add(2, 'days').startOf('day');
      return firstUpcomingDay.format('YYYY-MM-DD');
    case 'onboarding':
      return 'Onboarding';
    case 'backlog':
    default:
      return '';
  }
};

export var isDateInsideWorkingHours = function isDateInsideWorkingHours(date, workingHours) {
  var dateMoment = moment(date);
  var numWeekday = dateMoment.weekday();
  var weekday = constants.weekdays[numWeekday];
  var dailyWorkingHours = workingHours[weekday];
  if (!dailyWorkingHours.active) {
    return false;
  }

  var workStart = dailyWorkingHours.workStart;
  var workEnd = dailyWorkingHours.workEnd;
  var startOfWorkingDay = moment(date).hour(workStart.hour).minute(workStart.minute).seconds(0);
  var endOfWorkingDay = moment(date).hour(workEnd.hour).minute(workEnd.minute).seconds(0);

  return date >= startOfWorkingDay && date < endOfWorkingDay;
};

// see https://embed.plnkr.co/plunk/mWsW9q
export var dateDiff = function dateDiff(startdate, enddate) {
  //define moments for the startdate and enddate
  var startdateMoment = moment(startdate).startOf('day');
  var enddateMoment = moment(enddate).startOf('day');

  if (startdateMoment.isValid() === true && enddateMoment.isValid() === true) {
    //getting the difference in years
    var years = enddateMoment.diff(startdateMoment, 'years');

    //to calculate the months, first get the previous year and then subtract it
    startdateMoment.add(years, 'years');
    var months = enddateMoment.diff(startdateMoment, 'months');

    //to calculate the days, first get the previous month and then subtract it
    startdateMoment.add(months, 'months');
    var days = enddateMoment.diff(startdateMoment, 'days');

    return {
      years: years,
      months: months,
      days: days
    };
  }
  return undefined;
};
//# sourceMappingURL=datetime.js.map


//////////////////
// WEBPACK FOOTER
// ../gipsy-misc/lib/utils/datetime.js
// module id = 144
// module chunks = 0